import { type SupportedLocale } from '@zep-us/i18n';
import axios, { type AxiosResponse } from 'axios';

import { isProduction } from '@/consts/env';

const WEEGLOO_SPACE_ID = process.env.NEXT_PUBLIC_WEEGLOO_SPACE_ID;
const WEEGLOO_CDN_TOKEN = process.env.NEXT_PUBLIC_WEEGLOO_CDN_TOKEN;
const WEEGLOO_CMA_TOKEN = process.env.NEXT_PUBLIC_WEEGLOO_CMA_TOKEN;

// TODO(quiz-admin): quiz-admin now owns its Weegloo client copy.
// Remove admin-only CMA helpers from this quiz-app client after all admin imports
// are confirmed to use apps/quiz-admin-app/src/api/zepetoful.api.

/**
 * Weegloo CDA HTTP Instance (읽기 전용)
 * @description Content Delivery API - 퍼블리시된 콘텐츠 조회용
 * @see https://zep.us/weegloo/cda/v1/spaces/{SPACE_ID}/contents/
 */
const WEEGLOO_CDA_INSTANCE = axios.create({
  baseURL: `https://zep.us/weegloo/cda/v1/spaces/${WEEGLOO_SPACE_ID}/contents/`,
  headers: {
    Authorization: `Bearer ${WEEGLOO_CDN_TOKEN}`,
  },
});

/**
 * CDA API 응답 타입 (locale-unwrapped 형태)
 * CDA는 쿼리 파라미터로 locale을 받아서 해당 locale 데이터만 직접 반환합니다.
 */
type WeeglooContentCDAResponse<T = object> = {
  metadata: { tags: [] };
  fields: {
    json: T;
    jsonDev: T;
    title: string;
  };
  sys: {
    version: number;
  };
};

/**
 * Weegloo CDA에서 퍼블리시된 콘텐츠를 조회합니다.
 * @description Content Delivery API 사용 (읽기 전용)
 * @param entryId 요청할 Weegloo Content ID
 * @param locale 조회할 locale (optional, 기본값: en-US)
 * @returns {Promise<T>} 바로 데이터 반환
 */
export const getZepetofulCDNContent = async <T = object>({
  entryId,
  locale = 'en-US',
}: {
  entryId: string;
  locale?: SupportedLocale | 'en-US';
}): Promise<T> => {
  const res = await WEEGLOO_CDA_INSTANCE.get<WeeglooContentCDAResponse<T>>(
    entryId,
    {
      params: {
        locale: locale === 'en' ? 'en-US' : locale,
        select: isProduction ? 'fields.json' : 'fields.jsonDev',
      },
    },
  );
  const jsonData = res.data.fields.json ?? res.data.fields.jsonDev;
  return replaceWeeglooCdnUrl(jsonData) as T;
};

/**
 * Weegloo CMA HTTP Instance (읽기/쓰기)
 * @description Content Management API - Admin 전용, 콘텐츠 수정/발행용
 * @see https://zep.us/weegloo/cma/v1/spaces/{SPACE_ID}/contents/
 */
const WEEGLOO_CMA_INSTANCE = axios.create({
  baseURL: `https://zep.us/weegloo/cma/v1/spaces/${WEEGLOO_SPACE_ID}/contents/`,
  headers: {
    Authorization: `Bearer ${WEEGLOO_CMA_TOKEN}`,
  },
});

/**
 * CMA API 응답 타입 (locale-wrapped 형태)
 * CMA는 locale 데이터를 { [locale]: value } 형태로 반환합니다.
 */
type WeeglooContentCMAResponse<T = object> = {
  metadata: { tags: [] };
  fields: {
    json: { 'en-US': T };
    jsonDev: { 'en-US': T };
    title: { 'en-US': string };
  };
  sys: {
    version: number;
  };
};

/**
 * Weegloo CMA에서 콘텐츠를 조회합니다 (퍼블리쉬 직전 데이터 포함).
 * @warning Admin 전용 - 일반 조회는 getZepetofulCDNContent 사용
 * @description Content Management API 사용 (읽기/쓰기)
 */
export const _getZepetofulContent = (contentId: string) =>
  WEEGLOO_CMA_INSTANCE.get<WeeglooContentCMAResponse>(contentId);

type WeeglooContentCMARequest = Omit<WeeglooContentCMAResponse, 'sys'>;

/**
 * Weegloo CMA 콘텐츠를 업데이트합니다.
 *
 * @param {Object} params - 업데이트에 필요한 파라미터입니다.
 * @param {string} params.contentId - 업데이트할 콘텐츠의 ID입니다.
 * @param {object} params.data - 업데이트할 JSON 데이터입니다.
 * @param {string} params.title - 콘텐츠에 설정할 제목입니다.
 * @returns {Promise<WeeglooContentResponse>} Weegloo 콘텐츠 응답을 반환하는 Promise입니다.
 */
export const updateZepetofulContent = async ({
  contentId,
  data,
  title,
}: {
  contentId: string;
  data: object;
  title: string;
}): Promise<WeeglooContentCMAResponse> => {
  const getRes = await _getZepetofulContent(contentId);

  const res = await WEEGLOO_CMA_INSTANCE.put<
    WeeglooContentCMAResponse,
    AxiosResponse<WeeglooContentCMAResponse>,
    WeeglooContentCMARequest
  >(
    contentId,
    {
      metadata: { tags: [] },
      fields: {
        json: { 'en-US': data },
        jsonDev: { 'en-US': data },
        title: { 'en-US': title },
      },
    },
    {
      headers: {
        'Content-Type': 'application/vnd.com.weegloo.v1+json;charset=UTF-8',
        'X-Weegloo-Version': getRes.data.sys.version,
      },
    },
  );

  return res.data;
};

/**
 * Weegloo CMA에서 콘텐츠를 발행합니다.
 * @warning Admin 전용
 * @description Content Management API 사용
 */
export const publishZepetofulContent = async ({
  contentId,
}: {
  contentId: string;
}): Promise<WeeglooContentCMAResponse> => {
  const getRes = await _getZepetofulContent(contentId);
  return WEEGLOO_CMA_INSTANCE.put(
    `${contentId}/publish`,
    {},
    {
      headers: {
        'X-Weegloo-Version': getRes.data.sys.version,
      },
    },
  );
};

export const replaceWeeglooCdnUrl = (data: unknown): unknown => {
  const CMS_CDN_BASE_URL = process.env.NEXT_PUBLIC_CMS_CDN_BASE_URL;

  if (typeof data === 'string') {
    if (!CMS_CDN_BASE_URL) return data;
    return data.replace(
      /https:\/\/(?:files\.weegloo\.com|weegloo-media\.com)/g,
      CMS_CDN_BASE_URL,
    );
  }

  if (Array.isArray(data)) {
    return data.map(item => replaceWeeglooCdnUrl(item));
  }

  if (data && typeof data === 'object') {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        replaceWeeglooCdnUrl(value),
      ]),
    );
  }

  return data;
};
